import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  CheckboxFilterData,
  Filter,
  RadioButtonFilterData,
  RangeSliderFilterData,
  SliderFilterData,
  RangeSliderUpdateFilterEvent,
  SliderUpdateFilterEvent,
  CheckboxUpdateFilterEvent,
  RadioButtonUpdateFilterEvent,
} from '../filters';

export const enum FlightsFilterType {
  ArrivalTime = 'ArrivalTime',
  Carriers = 'Carriers',
  DepartureTime = 'DepartureTime',
  NumberOfStops = 'NumberOfStops',
  Price = 'Price',
  TravelDuration = 'TravelDuration',
}

export const enum FlightsFiltersEventType {
  UpdateFilter = 'UpdateFilter',
}

export interface ArrivalTimeFilter extends Filter<FlightsFilterType> {
  filterType: FlightsFilterType.ArrivalTime;
  filters: RangeSliderFilterData[];
  label: string;
}

export interface CarriersFilter extends Filter<FlightsFilterType> {
  filterType: FlightsFilterType.Carriers;
  label: string;
  data: CarrersFilterData[];
}

interface CarrersFilterData {
  filters: CheckboxFilterData[];
  title: string;
  ingredientId: IngredientId;
}

export interface DepartureTimeFilter extends Filter<FlightsFilterType> {
  filterType: FlightsFilterType.DepartureTime;
  filters: RangeSliderFilterData[];
  label: string;
}

export interface NumberOfStopsFilter extends Filter<FlightsFilterType> {
  filterType: FlightsFilterType.NumberOfStops;
  filters: RadioButtonFilterData[];
  label: string;
}

export interface PriceFilter extends Filter<FlightsFilterType> {
  filterType: FlightsFilterType.Price;
  filters: SliderFilterData[];
  label?: string;
}

export interface TravelDurationFilter extends Filter<FlightsFilterType> {
  filterType: FlightsFilterType.TravelDuration;
  filters: SliderFilterData[];
  label: string;
}

export interface FlightsFilters {
  ingredientsId: IngredientId[];
  arrivalTime: ArrivalTimeFilter;
  carriers: CarriersFilter;
  departureTime: DepartureTimeFilter;
  numberOfStops: NumberOfStopsFilter;
  price: PriceFilter;
  travelDuration: TravelDurationFilter;
}

interface UpdateFilterEvent<T> {
  eventType: FlightsFiltersEventType.UpdateFilter;
  filterType: FlightsFilterType;
  data: T;
}

export interface ArrivalTimeUpdateFilterEvent
  extends UpdateFilterEvent<RangeSliderUpdateFilterEvent> {
  eventType: FlightsFiltersEventType.UpdateFilter;
  filterType: FlightsFilterType.ArrivalTime;
}

export interface CarriersFilterUpdateFilterEvent
  extends UpdateFilterEvent<CheckboxUpdateFilterEvent> {
  eventType: FlightsFiltersEventType.UpdateFilter;
  filterType: FlightsFilterType.Carriers;
}

export interface DepartureTimeUpdateFilterEvent
  extends UpdateFilterEvent<RangeSliderUpdateFilterEvent> {
  eventType: FlightsFiltersEventType.UpdateFilter;
  filterType: FlightsFilterType.DepartureTime;
}

export interface NumberOfStopsUpdateFilterEvent
  extends UpdateFilterEvent<RadioButtonUpdateFilterEvent> {
  eventType: FlightsFiltersEventType.UpdateFilter;
  filterType: FlightsFilterType.NumberOfStops;
}

export interface PriceFilterUpdateFilterEvent
  extends UpdateFilterEvent<SliderUpdateFilterEvent> {
  eventType: FlightsFiltersEventType.UpdateFilter;
  filterType: FlightsFilterType.Price;
}

export interface TravelDurationUpdateFilterEvent
  extends UpdateFilterEvent<SliderUpdateFilterEvent> {
  eventType: FlightsFiltersEventType.UpdateFilter;
  filterType: FlightsFilterType.TravelDuration;
}

export type FlightsFilterEvents =
  | ArrivalTimeUpdateFilterEvent
  | CarriersFilterUpdateFilterEvent
  | DepartureTimeUpdateFilterEvent
  | NumberOfStopsUpdateFilterEvent
  | PriceFilterUpdateFilterEvent
  | TravelDurationUpdateFilterEvent;

export const enum FiltersModalEventType {
  CloseFiltersModal = 'CloseFiltersModal',
}

export interface FiltersModalEvent {
  eventType: FiltersModalEventType.CloseFiltersModal;
}

export type FiltersModalEvents = FiltersModalEvent | FlightsFilterEvents;
